import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Users, ChevronRight, Filter, X, Mail, GitCompare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import CompareBar from '../components/CompareBar';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import LocalServiceZone from '../components/LocalServiceZone';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Load comparison from localStorage
const loadCompareSpas = () => {
  try {
    const saved = localStorage.getItem('compareSpas');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Save comparison to localStorage
const saveCompareSpas = (spas) => {
  localStorage.setItem('compareSpas', JSON.stringify(spas));
};

const categories = [
  { value: 'all', label: 'Tous les spas' },
  { value: 'spa-massage', label: 'Spas de massage' },
  { value: 'spa-nage', label: 'Spas de nage' },
  { value: 'spa-debordement', label: 'Spas à débordement' },
  { value: 'bain-froid', label: 'Bains froids' },
];

const gammes = [
  { value: 'all', label: 'Toutes gammes' },
  { value: 'Essentiel', label: 'Gamme Essentiel' },
  { value: 'Classic', label: 'Gamme Classic' },
  { value: 'Premium', label: 'Gamme Premium' },
  { value: 'Débordement', label: 'Débordement' },
  { value: 'SMAX', label: 'SMAX (Nage)' },
  { value: 'EMAX', label: 'EMAX (Nage Bizone)' },
  { value: 'Bains Froids', label: 'Bains Froids' },
];

// Ordre de tri des gammes (du plus accessible au plus premium)
const gammeOrder = {
  'Essentiel': 1,
  'Classic': 2,
  'Premium': 3,
  'Débordement': 4,
  'SMAX': 5,
  'EMAX': 6,
  'Bains Froids': 7
};

const placesOptions = [
  { value: 'all', label: 'Toutes places' },
  { value: '2', label: '2 places' },
  { value: '3', label: '3 places' },
  { value: '4', label: '4 places' },
  { value: '5', label: '5 places' },
  { value: '6', label: '6+ places' },
];

const SpasPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [spas, setSpas] = useState([]);
  const [filteredSpas, setFilteredSpas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [places, setPlaces] = useState(searchParams.get('places') || 'all');
  const [gamme, setGamme] = useState(searchParams.get('gamme') || 'all');
  const [compareSpas, setCompareSpas] = useState(loadCompareSpas());

  // Toggle spa in comparison
  const toggleCompare = (spa, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const isSelected = compareSpas.some(s => s.slug === spa.slug);
    let newCompareSpas;
    
    if (isSelected) {
      newCompareSpas = compareSpas.filter(s => s.slug !== spa.slug);
    } else if (compareSpas.length < 3) {
      newCompareSpas = [...compareSpas, { 
        slug: spa.slug, 
        name: spa.name, 
        places: spa.places,
        images: spa.images 
      }];
    } else {
      return; // Max 3 spas
    }
    
    setCompareSpas(newCompareSpas);
    saveCompareSpas(newCompareSpas);
  };

  const removeFromCompare = (slug) => {
    const newCompareSpas = compareSpas.filter(s => s.slug !== slug);
    setCompareSpas(newCompareSpas);
    saveCompareSpas(newCompareSpas);
  };

  const clearCompare = () => {
    setCompareSpas([]);
    saveCompareSpas([]);
  };

  const isInCompare = (slug) => compareSpas.some(s => s.slug === slug);

  useEffect(() => {
    const fetchSpas = async () => {
      try {
        const response = await axios.get(`${API}/spas`);
        setSpas(response.data);
        setFilteredSpas(response.data);
      } catch (error) {
        console.error('Error fetching spas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpas();
  }, []);

  useEffect(() => {
    let result = [...spas];
    
    if (category !== 'all') {
      result = result.filter(spa => spa.category === category);
    }

    if (gamme !== 'all') {
      result = result.filter(spa => spa.gamme === gamme);
    }
    
    if (places !== 'all') {
      const placesNum = parseInt(places);
      if (placesNum === 6) {
        result = result.filter(spa => spa.places >= 6);
      } else {
        result = result.filter(spa => spa.places === placesNum);
      }
    }
    
    // Trier par gamme puis par nombre de places
    result.sort((a, b) => {
      const orderA = gammeOrder[a.gamme] || 99;
      const orderB = gammeOrder[b.gamme] || 99;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      // Si même gamme, trier par nombre de places
      return (a.places || 0) - (b.places || 0);
    });
    
    setFilteredSpas(result);
    
    // Update URL params
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (gamme !== 'all') params.set('gamme', gamme);
    if (places !== 'all') params.set('places', places);
    setSearchParams(params);
  }, [category, gamme, places, spas, setSearchParams]);

  const clearFilters = () => {
    setCategory('all');
    setGamme('all');
    setPlaces('all');
  };

  const hasFilters = category !== 'all' || gamme !== 'all' || places !== 'all';

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="spas-page">
      <SEO 
        title="Nos Spas - Catalogue complet"
        description="Découvrez notre gamme de spas Origin Spa : spas de massage, spas de nage, bains froids. Sélection rigoureuse par notre technicien spécialiste. Livraison et installation dans le Grand Ouest."
        canonical="/spas"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Nos Spas</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Section - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image={realPhotos.heroMain}
        imageAlt={realPhotosAlt.heroMain}
        title="Notre gamme de spas"
        subtitle="Des spas sélectionnés pour leur fiabilité, leur durabilité et la disponibilité des pièces détachées."
        overlayPosition="left"
      />

      {/* Local Service Zone Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <LocalServiceZone />
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-[#E2E8F0] sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[#475569]">
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filtres :</span>
            </div>
            
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[180px] rounded-lg border-[#E2E8F0]" data-testid="category-filter">
                <SelectValue placeholder="Type de spa" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={gamme} onValueChange={setGamme}>
              <SelectTrigger className="w-[180px] rounded-lg border-[#E2E8F0]" data-testid="gamme-filter">
                <SelectValue placeholder="Gamme" />
              </SelectTrigger>
              <SelectContent>
                {gammes.map(g => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={places} onValueChange={setPlaces}>
              <SelectTrigger className="w-[150px] rounded-lg border-[#E2E8F0]" data-testid="places-filter">
                <SelectValue placeholder="Nb de places" />
              </SelectTrigger>
              <SelectContent>
                {placesOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#64748B] gap-1 hover:text-[#0F172A]">
                <X className="w-4 h-4" />
                Effacer
              </Button>
            )}

            <div className="ml-auto text-base text-[#64748B]">
              {filteredSpas.length} spa{filteredSpas.length > 1 ? 's' : ''} trouvé{filteredSpas.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => (
              <Card key={i} className="animate-pulse rounded-xl">
                <div className="h-56 bg-[#E2E8F0]" />
                <CardContent className="p-5">
                  <div className="h-5 bg-[#E2E8F0] rounded mb-3" />
                  <div className="h-4 bg-[#E2E8F0] rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSpas.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#475569] mb-5 text-lg">Aucun spa ne correspond à vos critères.</p>
            <Button onClick={clearFilters} className="bg-[#1E3A5F] hover:bg-[#152C4A] rounded-lg">Voir tous les spas</Button>
          </div>
        ) : (
          <>
            {/* Group spas by gamme */}
            {Object.entries(
              filteredSpas.reduce((acc, spa) => {
                const g = spa.gamme || 'Autre';
                if (!acc[g]) acc[g] = [];
                acc[g].push(spa);
                return acc;
              }, {})
            )
            .sort(([a], [b]) => (gammeOrder[a] || 99) - (gammeOrder[b] || 99))
            .map(([gammeName, spasInGamme]) => (
              <div key={gammeName} className="mb-12">
                {/* Gamme Section Header */}
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-[#0F172A]">
                    {gammeName === 'Essentiel' && 'Gamme Essentiel'}
                    {gammeName === 'Classic' && 'Gamme Classic'}
                    {gammeName === 'Premium' && 'Gamme Premium'}
                    {gammeName === 'Débordement' && 'Spas à Débordement'}
                    {gammeName === 'SMAX' && 'Spas de Nage SMAX'}
                    {gammeName === 'EMAX' && 'Spas de Nage EMAX (Bizone)'}
                    {gammeName === 'Bains Froids' && 'Bains Froids'}
                    {!['Essentiel', 'Classic', 'Premium', 'Débordement', 'SMAX', 'EMAX', 'Bains Froids'].includes(gammeName) && gammeName}
                  </h2>
                  <div className="flex-1 h-px bg-[#E2E8F0]" />
                  <span className="text-sm text-[#64748B] font-medium">
                    {spasInGamme.length} modèle{spasInGamme.length > 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Spas Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {spasInGamme.map((spa) => (
                    <div key={spa.id} className="relative">
                      <Link to={`/spas/${spa.slug}`}>
                        <Card className="card-hover border-[#E2E8F0] bg-white overflow-hidden h-full rounded-xl" data-testid={`spa-card-${spa.slug}`}>
                          {/* Image - NO CROPPING */}
                          <div className="bg-white h-56 relative flex items-center justify-center">
                            <img 
                              src={spa.images[0] || 'https://images.unsplash.com/photo-1574186907057-849802af13d9?w=400'} 
                              alt={spa.name}
                              className="w-full h-full object-contain p-3"
                              loading="lazy"
                            />
                            {spa.in_stock && (
                              <span className="absolute top-3 right-3 bg-[#10B981] text-white text-xs font-semibold px-3 py-1.5 rounded-lg">En stock</span>
                            )}
                            <div className="absolute top-3 left-3">
                              <span className="bg-[#0F172A]/90 text-white text-xs px-3 py-1.5 rounded-lg font-medium">
                                {categories.find(c => c.value === spa.category)?.label || spa.category}
                              </span>
                            </div>
                          </div>
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-[#64748B] uppercase tracking-wide font-medium">{spa.brand}</span>
                              {spa.gamme && (
                                <span className="text-xs font-semibold text-[#1E3A5F] bg-[#1E3A5F]/10 px-2.5 py-1 rounded-lg">
                                  {spa.gamme}
                                </span>
                              )}
                            </div>
                            <h3 className="font-semibold text-[#0F172A] text-lg mb-3 line-clamp-2">{spa.name}</h3>
                            <div className="flex items-center gap-4 text-base text-[#475569] mb-4">
                              <span className="flex items-center gap-1.5">
                                <Users className="w-4 h-4" />
                                {spa.places} places
                              </span>
                              <span className="text-sm">{spa.dimensions}</span>
                            </div>
                            <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
                              {spa.price_on_request ? (
                                <span className="text-[#F97316] font-semibold">Prix sur devis</span>
                              ) : (
                                <span className="text-2xl font-bold text-[#0F172A]">{formatPrice(spa.price)}</span>
                              )}
                              <span className="text-[#1E3A5F] font-semibold flex items-center gap-1">
                                Voir <ChevronRight className="w-4 h-4" />
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                      {/* Compare checkbox */}
                      <button
                        onClick={(e) => toggleCompare(spa, e)}
                        className={`absolute top-3 right-3 z-10 p-2 rounded-lg transition-all ${
                          isInCompare(spa.slug)
                            ? 'bg-[#1E3A5F] text-white'
                            : 'bg-white/90 text-[#64748B] hover:bg-white hover:text-[#1E3A5F]'
                        } ${compareSpas.length >= 3 && !isInCompare(spa.slug) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={compareSpas.length >= 3 && !isInCompare(spa.slug)}
                        title={isInCompare(spa.slug) ? 'Retirer du comparateur' : 'Ajouter au comparateur'}
                        style={{ top: '60px' }}
                      >
                        <GitCompare className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Compare Bar */}
      <CompareBar 
        selectedSpas={compareSpas} 
        onRemove={removeFromCompare} 
        onClear={clearCompare} 
      />

      {/* CTA Banner */}
      <div className="bg-[#1E3A5F] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Besoin de conseils pour choisir votre spa ?
          </h2>
          <p className="text-[#94A3B8] mb-8 text-lg">
            Notre technicien vous guide vers le modèle adapté à votre projet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] gap-2 rounded-lg h-12 font-semibold">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/devis">
              <Button size="lg" variant="outline" className="border-white/25 text-white hover:bg-white/10 rounded-lg h-12">
                Demander un Devis
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Schema.org ItemList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Spas Atlantique Spa Boutique",
          "description": "Gamme de spas sélectionnés par notre technicien expert",
          "numberOfItems": filteredSpas.length,
          "itemListElement": filteredSpas.slice(0, 10).map((spa, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "Product",
              "name": spa.name,
              "description": spa.description,
              "brand": { "@type": "Brand", "name": spa.brand }
            }
          }))
        })
      }} />
      
      {/* Schema.org BreadcrumbList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": "https://atlantiquespaboutique.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Nos Spas",
              "item": "https://atlantiquespaboutique.com/spas"
            }
          ]
        })
      }} />
    </div>
  );
};

export default SpasPage;
